import { useForm, FormProvider, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import BaseInput from "../../../shared/components/BaseInput";
import { useIndustrySpecialists } from "../../../industry-specialist/hooks/useIndustrySpecialists";
import BaseButton from "components/common/BaseButton";
import { useIntl } from "react-intl";
import { useNAMStates } from "pages/admin-app/states/hooks/useNAMStates";
import BaseAsyncPaginate from "pages/admin-app/shared/components/BaseAsyncPaginate";
import { loadIndustrySpecialists } from "pages/admin-app/industry-specialist/services/loadIndustrySpecialists";
import BaseSelect from "pages/admin-app/shared/components/BaseSelect";
import {
  EndUserFormData,
  getEndUserFormSchema,
} from "./create-end-user.schema";
import { loadTerritoryManagers } from "pages/admin-app/territory-manager/services/loadTerritoryManagers";
import { loadDistributors } from "pages/admin-app/distributor/services/loadDistributors";
import { useTerritoryManagers } from "pages/admin-app/territory-manager/hooks/useTerritoryManagers";
import { useDistributors } from "pages/admin-app/distributor/hooks/useDistributors";
import { useCountries } from "pages/admin-app/country/hooks/useCountries";
import { loadCountries } from "pages/admin-app/country/services/loadCountries";
import { loadStates } from "pages/admin-app/states/services/loadStates";
import { useEffect } from "react";

interface Props {
  onSubmit: (data: EndUserFormData) => void;
  isSubmitting: boolean;
}

const CreateEndUserForm = ({ onSubmit, isSubmitting }: Props) => {
  const intl = useIntl();
  const endUserFormSchema = getEndUserFormSchema(intl);

  const methods = useForm({
    resolver: zodResolver(endUserFormSchema),
  });

  const { handleSubmit, control, setValue, clearErrors } = methods;
  const selectedCountry = useWatch({ control, name: "countryId" });

  useEffect(() => {
    setValue("state", "");
    clearErrors("state");
  }, [selectedCountry, setValue, clearErrors]);

  const {
    data: industrySpecialists,
    isLoading: isLoadingSpecialists,
    isError: isErrorSpecialists,
    error: specialistsError,
    refetch: refetchIndustrySpecialists,
    isFetching: isFetchingSpecialists,
  } = useIndustrySpecialists({ currentPage: 1 });

  const {
    data: territoryManagers,
    isLoading: isLoadingTerritoryManagers,
    isError: isErrorManagers,
    error: managersError,
    refetch: refetchTerritoryManagers,
    isFetching: isFetchingManagers,
  } = useTerritoryManagers({ currentPage: 1 });

  const {
    data: distributors,
    isLoading: isLoadingDistributors,
    isError: isErrorDistributors,
    error: distributorsError,
    refetch: refetchDistributors,
    isFetching: isFetchingDistributors,
  } = useDistributors({ currentPage: 1 });

  const {
    data: states,
    isLoading: isLoadingStates,
    error: statesError,
    isError: isErrorStates,
    refetch: refetchStates,
    isFetching: isFetchingStates,
  } = useNAMStates({
    countryId: selectedCountry,
    currentPage: 1,
  });

  const {
    data: countries,
    isLoading: isLoadingCountries,
    isError: isErrorCountries,
    error: countriesError,
    refetch: refetchCountries,
    isFetching: isFetchingCountries,
  } = useCountries({ currentPage: 1 });

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="inputs-container">
        <BaseInput
          name="name"
          label={intl.formatMessage({ id: "usAdmin.name" })}
          type="text"
          containerClassName="usadmin-input-name"
        />
        <BaseInput
          name="contactName"
          label={intl.formatMessage({ id: "usAdmin.contactName.endUser" })}
          type="text"
          containerClassName="usadmin-input-contactName"
        />
        <BaseInput
          name="address"
          label={intl.formatMessage({ id: "usAdmin.address" })}
          type="text"
          containerClassName="second-column"
        />
        <div className="third-row">
          <BaseInput
            name="email"
            label={intl.formatMessage({ id: "usAdmin.email.endUser" })}
            type="text"
          />
          <BaseInput
            name="phone"
            label={intl.formatMessage({ id: "usAdmin.phone" })}
            type="text"
          />
          <BaseInput
            name="zip"
            label={intl.formatMessage({ id: "usAdmin.zip" })}
            type="text"
          />
        </div>
        <div className="third-row">
          <BaseInput
            name="city"
            label={intl.formatMessage({ id: "usAdmin.city" })}
            type="text"
          />
          <BaseAsyncPaginate
            name="state"
            label={intl.formatMessage({ id: "usAdmin.state" })}
            loadOptions={loadStates(selectedCountry)}
            data={states?.data || []}
            isLoading={isLoadingStates || isFetchingStates}
            isError={isErrorStates}
            error={statesError}
            refetch={refetchStates}
            optionMapper={(state) => ({
              value: state.abbr,
              label: `${state.name} (${state.abbr})`,
            })}
            placeholder={intl.formatMessage({ id: "usAdmin.selectState" })}
            isDisabled={!selectedCountry}
            selectedCountry={selectedCountry}
          />
          <BaseAsyncPaginate
            name="countryId"
            label={intl.formatMessage({ id: "country" })}
            loadOptions={loadCountries}
            data={countries?.data || []}
            additional={{ page: 1 }}
            isLoading={isLoadingCountries || isFetchingCountries}
            isError={isErrorCountries}
            error={countriesError}
            optionMapper={(country) => ({
              value: country.id,
              label: country.name,
            })}
            refetch={refetchCountries}
            placeholder={intl.formatMessage({ id: "usAdmin.selectCountry" })}
          />
        </div>
        <div className="forth-row">
          <BaseAsyncPaginate
            name="isId"
            label={intl.formatMessage({ id: "usAdmin.industrySpecialist" })}
            loadOptions={loadIndustrySpecialists}
            data={industrySpecialists?.data || []}
            optionMapper={(state) => ({
              value: state.id,
              label: state.name,
            })}
            additional={{ page: 1 }}
            isLoading={isLoadingSpecialists || isFetchingSpecialists}
            isError={isErrorSpecialists}
            error={specialistsError}
            refetch={refetchIndustrySpecialists}
            placeholder={intl.formatMessage({
              id: "usAdmin.selectIndustrySpecialist",
            })}
          />
          <BaseAsyncPaginate
            name="tmId"
            label={intl.formatMessage({ id: "usAdmin.territoryManager" })}
            loadOptions={loadTerritoryManagers}
            data={territoryManagers?.data || []}
            optionMapper={(state) => ({
              value: state.id,
              label: state.name,
            })}
            additional={{ page: 1 }}
            isLoading={isLoadingTerritoryManagers || isFetchingManagers}
            isError={isErrorManagers}
            error={managersError}
            refetch={refetchTerritoryManagers}
            placeholder={intl.formatMessage({
              id: "usAdmin.selectTerritoryManager",
            })}
          />
          <BaseAsyncPaginate
            name="distributorId"
            label={intl.formatMessage({ id: "usAdmin.distributor" })}
            loadOptions={loadDistributors}
            data={distributors?.data || []}
            optionMapper={(state) => ({
              value: state.id,
              label: `${state.contactName} @ ${state.name}`,
            })}
            additional={{ page: 1 }}
            isLoading={isLoadingDistributors || isFetchingDistributors}
            isError={isErrorDistributors}
            error={distributorsError}
            refetch={refetchDistributors}
            placeholder={intl.formatMessage({
              id: "usAdmin.selectDistributor",
            })}
          />
          <BaseSelect
            name="automatedRg"
            label={intl.formatMessage({ id: "usAdmin.automatedRg" })}
            data={[false, true]}
            optionMapper={(option) => ({
              value: option,
              label: option
                ? intl.formatMessage({ id: "customComponents.yes" })
                : intl.formatMessage({ id: "customComponents.no" }),
            })}
          />
        </div>
        <BaseButton
          text={intl.formatMessage({ id: "usAdmin.create" })}
          type="submit"
          disabled={isSubmitting}
          className="first-column usadmin-form-submit-button"
          handleSubmit={() => {}}
        />
      </form>
    </FormProvider>
  );
};

export default CreateEndUserForm;
